/**
 * The shape of a building's floor as the camera sees it (no drawing here, so it is tested in Node):
 * its plan turned to the camera's angle, which walls stand and which are cut down so the room
 * stays in view, the pieces of furniture, the flights of stairs and how light falls across it.
 * The art is painted from this in interior.ts.
 */
import { SOLID_CELLS, WALL_CELLS } from '@nine/shared';

/** Map coordinates ↔ coordinates of the map as seen at the camera's angle (IsoCamera). */
export interface Turning {
  turn(x: number, y: number): [number, number];
  unturn(x: number, y: number): [number, number];
}

/** A floor's plan (map.interior.plan, n×n) as seen at the camera's angle: row j, column i. */
export function viewPlan(plan: readonly string[], n: number, t: Turning): string[] {
  const rows: string[] = [];
  for (let j = 0; j < n; j++) {
    let row = '';
    for (let i = 0; i < n; i++) {
      // A view cell's centre goes back to the map cell under it.
      const [x, y] = t.unturn(i + 0.5, j + 0.5);
      row += plan[Math.floor(y)]?.[Math.floor(x)] ?? ' ';
    }
    rows.push(row);
  }
  return rows;
}

export const cellAt = (v: readonly string[], i: number, j: number): string => v[j]?.[i] ?? ' ';
export const isWall = (ch: string): boolean => ch !== ' ' && WALL_CELLS.includes(ch);
export const isSolidCell = (ch: string): boolean => ch !== ' ' && SOLID_CELLS.includes(ch);
/**
 * A cell of the room itself: what a wall is judged against. The balcony and its rail are outside
 * the walls, so a wall with the balcony in front of it and the room behind is still a front wall.
 */
export const isRoom = (ch: string): boolean => ch !== ' ' && ch !== 'b' && ch !== '=' && !isWall(ch);

/**
 * How a wall is drawn: `back` walls (the room in front of them) stand full height, `front` walls
 * (the room behind them, between it and the camera) are cut down low, `partition` walls (room on
 * both sides) stand and turn see-through over the player, and the `side` corners at the far left
 * and right stand too, since nothing of the room is behind them.
 */
export type WallCut = 'back' | 'front' | 'partition' | 'side';

export function wallCut(v: readonly string[], i: number, j: number): WallCut {
  const room = (a: number, b: number) => isRoom(cellAt(v, a, b));
  const front = room(i + 1, j) || room(i, j + 1) || room(i + 1, j + 1);
  const back = room(i - 1, j) || room(i, j - 1) || room(i - 1, j - 1);
  if (front && back) return 'partition';
  if (back) return 'front';
  if (front) return 'back';
  return 'side';
}

/** Whether a wall is cut down (drawn as a stub showing its section) rather than standing. */
export const isCut = (v: readonly string[], i: number, j: number): boolean => wallCut(v, i, j) === 'front';

/** A piece of furniture: a run of joined cells of one kind (a long table, a counter, a bed). */
export interface Piece {
  readonly ch: string;
  /** North corner and size of the footprint, in view cells. */
  readonly i: number;
  readonly j: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Furniture as pieces: cells of the kinds in `joined` that touch along a row or a column are one
 * piece (a straight run only; a corner starts a new one), every other kind one piece a cell.
 */
export function pieces(v: readonly string[], kinds: string, joined: string): Piece[] {
  const out: Piece[] = [];
  const taken = new Set<number>();
  const n = v.length;
  for (let j = 0; j < n; j++)
    for (let i = 0; i < (v[j]?.length ?? 0); i++) {
      const ch = cellAt(v, i, j);
      if (!kinds.includes(ch) || taken.has(j * 4096 + i)) continue;
      let w = 1;
      let h = 1;
      if (joined.includes(ch)) {
        while (cellAt(v, i + w, j) === ch && !taken.has(j * 4096 + i + w)) w++;
        if (w === 1) while (cellAt(v, i, j + h) === ch && !taken.has((j + h) * 4096 + i)) h++;
      }
      for (let b = 0; b < h; b++) for (let a = 0; a < w; a++) taken.add((j + b) * 4096 + i + a);
      out.push({ ch, i, j, w, h });
    }
  return out;
}

/**
 * Draw-order key of a vertical strip of a footprint's sprite, `u` tiles right of its north corner
 * on screen: the depth of the footprint's front edge under the strip (as buildings are cut).
 */
export function stripDepth(p: { i: number; j: number; w: number; h: number }, u: number): number {
  const c = Math.max(-p.h, Math.min(p.w, u));
  return p.i + p.j + 2 * Math.min(p.w, c + p.h) - c;
}

/**
 * Which way a flight of stairs climbs (or a stairwell goes down) in a cell: away from the open
 * floor it is stepped onto from. [di, dj] in view cells.
 */
export function flightDir(v: readonly string[], i: number, j: number): [number, number] {
  const ch = cellAt(v, i, j);
  for (const [di, dj] of [
    [-1, 0],
    [0, -1],
    [1, 0],
    [0, 1],
  ] as const) {
    const o = cellAt(v, i + di, j + dj);
    if (o !== ch && o !== ' ' && !isWall(o) && !isSolidCell(o) && !'^vuh'.includes(o)) return [-di, -dj];
  }
  return [1, 0];
}

/** A source of light on the floor (view cells): its strength at the source and its reach. */
export interface Lamp {
  readonly x: number;
  readonly y: number;
  readonly r: number;
  readonly k: number;
}

/**
 * How lit a point of the floor is (0 dark – 1 full): the room's own light (`ambient`, from how
 * dark the floor is) plus each lamp, falling off with the square of the distance to its reach.
 */
export function lightAt(x: number, y: number, ambient: number, lamps: readonly Lamp[]): number {
  let l = ambient;
  for (const p of lamps) {
    const d = Math.hypot(x - p.x, y - p.y) / p.r;
    if (d < 1) l += p.k * (1 - d) * (1 - d);
  }
  return l < 0 ? 0 : l > 1 ? 1 : l;
}
